import { Card, Divider, Flex, Image, Stack, Text, rem } from "@mantine/core";
import {
  Dialog,
  PrintableDropdownOptions,
  REPORT_LOGO_FLEX,
  REPORT_LOGO_HEIGHT,
  usePrintableContext,
} from "@components";
import { TestCategory, TestType } from "types/testTypes";
import {
  calculateAgeForTest,
  displayPatientName,
  formatDate,
  formatDateOnly,
  formatTime,
  parseDateOnly,
} from "@utils/CommonUtil";
import { getTestDuration } from "@utils/testDuration";
import { buildTestResultFilename } from "@utils/printableUtils";
import { useEffect, useMemo, useState } from "react";

import ChangePatientModal from "./ChangePatientModal";
import ConfirmChangePatientModal from "./ConfirmChangePatientModal";
import TestDetailsDialog from "./TestDetailsDialog";
import type { ContrastSensitivityTestResult } from "types/tests/specifics";
import { Patient } from "types/patientTypes";
import { changeTestPatient, deleteTest } from "@utils/api-communication/RestApiCli";
import { createStyles } from "@mantine/emotion";
import { getFnFpError } from "@utils";
import { getKineticPatternLabelKey } from "@utils/test-specific-utils/KineticPerimetryUtils";
import { selectPdfFilenameFormat } from "@store/userPreferences/userPreferencesSlice";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Keeps the logo clear of the absolutely positioned options button.
const OPTIONS_BUTTON_CLEARANCE = 96;

const useStyles = createStyles((theme, _, u) => ({
  text: {
    lineHeight: "1.2",
    [u.dark]: {
      color: theme.colors.dark[0],
    },

    [u.light]: {
      color: theme.colors.dark[6],
    },
  },

  middle: {
    textAlign: "center",
  },

  right: {
    textAlign: "right",
  },
}));

export default function ExtraTestEntryHeader(props) {
  const [patientAge, setPatientAge] = useState(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [finishDate, setFinishDate] = useState<Date | null>(null);
  const [categoryLabel, setCategoryLabel] = useState<String>("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [changingPatient, setChangingPatient] = useState(false);
  const [changePatientError, setChangePatientError] = useState<string | null>(null);
  const [changePatientOpened, { open: openChangePatient, close: closeChangePatient }] =
    useDisclosure(false);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const { entry: results } = props;
  const pdfFilenameFormat = useSelector(selectPdfFilenameFormat);
  const pdfFilename = buildTestResultFilename(results, pdfFilenameFormat);

  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { prettier } = usePrintableContext();

  const contrastFpFnInfo =
    results?.category === TestCategory.ContrastSensitivity &&
    results?.testType === TestType.Threshold
      ? ((results as ContrastSensitivityTestResult)?.fpFnInfo ??
        (results as ContrastSensitivityTestResult)?.results?.fpFnInfo)
      : null;

  useEffect(() => {
    const pAge = calculateAgeForTest(new Date(startDate), parseDateOnly(patient?.dob));

    setPatientAge(pAge);
    setPatient(results?.patient);
    setStartDate(results?.startDate);
    setFinishDate(results?.finishDate);

    const category = results?.category;
    if (category && category !== TestCategory.ColorVision) {
      setCategoryLabel(t("TestType." + results?.testType));
    } else if (category === TestCategory.ColorVision) {
      setCategoryLabel(t("TestType.Ishihara"));
    }
  }, [results, results?.patient, results?.startDate, results?.finishDate, t, patient?.dob]);

  function handleChangePatientSelect(newPatient: Patient) {
    setSelectedPatient(newPatient);
    closeChangePatient();
    openConfirm();
  }

  function handleConfirmChangePatient() {
    if (!results?.uuid || !selectedPatient) return;
    setChangingPatient(true);
    setChangePatientError(null);
    changeTestPatient(
      results.uuid,
      selectedPatient.uuid,
      () => {
        setChangingPatient(false);
        closeConfirm();
        setSelectedPatient(null);
        props.onPatientChanged?.();
      },
      () => {
        setChangingPatient(false);
        setChangePatientError(
          t("ResultList.changePatientError", "Failed to change patient. Please try again."),
        );
      },
    );
  }

  function handleCancelConfirm() {
    closeConfirm();
    setSelectedPatient(null);
    setChangePatientError(null);
  }

  function handleDelete() {
    if (!results?.uuid) return;
    deleteTest(
      { uuid: results.uuid },
      () => {
        navigate("/list");
        setShowDeleteConfirmation(false);
      },
      (err) => console.log("error on delete test", err),
      null,
    );
  }

  const dropdownOptions = useMemo(
    () => [
      { children: t("ResultList.testDetails"), onClick: () => setShowDetails(true) },
      { children: t("ResultList.changePatient"), onClick: openChangePatient },
      {
        children: t("ResultList.deleteTest"),
        onClick: () => setShowDeleteConfirmation(true),
      },
    ],
    [t, openChangePatient],
  );

  //todo: Handle this case with either a loading screen or better yet handle error and loading states globally

  return (
    <Card
      withBorder={prettier}
      radius="md"
      shadow={prettier ? "md" : "none"}
      bg={prettier ? undefined : "transparent"}
      mb={prettier ? "md" : 0}
    >
      <Stack>
        <Flex align={"center"} justify={"space-between"}>
          <Stack gap={0} mx={"md"}>
            <Text fz={rem(30)} fw={600} className={classes.text}>
              {patient && displayPatientName(patient?.name)}
            </Text>
            {patientAge && patient?.dob ? (
              <Text fz={rem(20)} fw={600} className={classes.text}>
                {t("ResultList.age") + " : " + patientAge + ` (${formatDateOnly(patient.dob)})`}
              </Text>
            ) : null}
          </Stack>
          <Image
            src="/img/logo.png"
            alt="Oculera"
            w="auto"
            h={REPORT_LOGO_HEIGHT}
            flex={REPORT_LOGO_FLEX}
            fit="contain"
            ml="auto"
            mr={rem(OPTIONS_BUTTON_CLEARANCE)}
          />
          <PrintableDropdownOptions
            filename={pdfFilename}
            showDownloadSettingsLink
            options={dropdownOptions}
          />
          <Dialog
            title={t("ResultList.deleteTestConfirmation")}
            open={showDeleteConfirmation}
            onCancel={setShowDeleteConfirmation}
            cancelLabel={t("ResultList.cancel")}
            onConfirm={handleDelete}
            confirmLabel={t("ResultList.deleteTest")}
          >
            <div>{t("ResultList.deleteTestConfirmationMessage")}</div>
          </Dialog>
          <ChangePatientModal
            opened={changePatientOpened}
            onClose={closeChangePatient}
            onConfirm={handleChangePatientSelect}
          />
          <ConfirmChangePatientModal
            opened={confirmOpened}
            onClose={handleCancelConfirm}
            onConfirm={handleConfirmChangePatient}
            currentPatient={patient}
            selectedPatient={selectedPatient}
            loading={changingPatient}
            error={changePatientError}
          />
          <TestDetailsDialog
            open={showDetails}
            onClose={setShowDetails}
            device={results?.device}
            version={results?.version}
          />
        </Flex>
        <Divider size={"md"} color={"blue"} />
        <Flex align={"flex-start"} justify={"space-between"} mx={"md"}>
          <Stack gap={0}>
            {results?.category && results.category != TestCategory.ColorVision && (
              <Text fw={600} fz={"lg"} className={classes.text}>
                {t("TestType." + results?.category)}
              </Text>
            )}
            <Text fz={"lg"} className={classes.text}>
              {startDate && formatDate(startDate)}
            </Text>
            <Text fz={"lg"} className={classes.text}>
              {startDate && formatTime(startDate)}
            </Text>
            {results?.kpConfig?.stimuliSize ? (
              <>
                <Text>
                  {t("TestController.stimuliSize")}:{" "}
                  {t("TestController.stimuliSize_" + results?.kpConfig?.stimuliSize)}
                </Text>
              </>
            ) : null}
          </Stack>
          {results?.eye && startDate && finishDate && (
            <Stack gap={0}>
              <Text fw={600} fz={"lg"} className={cx(classes.text, classes.middle)}>
                {t("TestController.eye")}
              </Text>
              <Text fz={"lg"} className={cx(classes.text, classes.middle)} mb={"sm"} c={"orange.6"}>
                {t(results?.eye)}
              </Text>
              <Text fw={600} fz={"lg"} className={cx(classes.text, classes.middle)}>
                {t("ResultList.duration")}
              </Text>
              <Text fz={"lg"} className={cx(classes.text, classes.middle)} mb={"sm"} c={"orange.6"}>
                {getTestDuration(startDate, finishDate)}
              </Text>
            </Stack>
          )}
          {results?.testType && (
            <Stack gap={0}>
              <Text fw={600} fz={"lg"} className={cx(classes.text, classes.right)}>
                {t(`TestType.testType`)}
              </Text>
              <Text fz={"lg"} className={cx(classes.text, classes.right)} mb={"sm"} c={"blue.6"}>
                {categoryLabel}
              </Text>
              {results.testPattern ? (
                <>
                  <Text fw={600} fz={"lg"} className={cx(classes.text, classes.right)}>
                    {t(`testPattern`)}
                  </Text>
                  <Text
                    fz={"lg"}
                    className={cx(classes.text, classes.right)}
                    mb={"sm"}
                    c={"blue.6"}
                  >
                    {t(getKineticPatternLabelKey(results.testPattern))}{" "}
                    {results.kpConfig.dBColor ? results.kpConfig.dBColor + " dB" : ""}
                  </Text>
                </>
              ) : null}
            </Stack>
          )}
          {contrastFpFnInfo && (
            <Stack gap={0}>
              <Text fw={600} fz={"lg"} className={cx(classes.text, classes.right)}>
                {t("ResultList.fpError")}
              </Text>
              <Text fz={"lg"} className={cx(classes.text, classes.right)} mb={"sm"} c={"blue.6"}>
                {getFnFpError({ fpFnInfo: contrastFpFnInfo }, "fp")}%
              </Text>
              <Text fw={600} fz={"lg"} className={cx(classes.text, classes.right)}>
                {t("ResultList.fnError")}
              </Text>
              <Text fz={"lg"} className={cx(classes.text, classes.right)} mb={"sm"} c={"blue.6"}>
                {getFnFpError({ fpFnInfo: contrastFpFnInfo }, "fn")}%
              </Text>
            </Stack>
          )}
        </Flex>
      </Stack>
    </Card>
  );
}
